import classNames from 'classnames';
import React, {useCallback, useMemo, useState} from 'react';

import {LocalizedHtml, useI18n} from '@udemy/i18n';
import NextIcon from '@udemy/icons/dist/next.ud-icon';
import PreviousIcon from '@udemy/icons/dist/previous.ud-icon';
import {IconButton} from '@udemy/react-core-components';
import {BlockQuoteCard} from '@udemy/react-product-card-components';
import {
    CarouselMutationEvent,
    CarouselSlide,
    CarouselWithFocusOrder,
    dispatchCarouselNextPageEvent,
    dispatchCarouselPreviousPageEvent,
    IndicatorDots,
} from '@udemy/react-structure-components';

import styles from './personal-plan-testimonials.module.less';

interface Testimonial {
    id: string;
    /** HTML string. Phrases wrapped in `<span class="highlight">` render bold. */
    quote: string;
    name: string;
}

/**
 * Social proof section rendered above the partners banner for the complete revamp.
 */
export const PersonalPlanTestimonials = () => {
    const {gettext} = useI18n();

    const testimonials = useMemo<Testimonial[]>(
        () => [
            {
                id: 'adarsh-a',
                quote: gettext(
                    'The <span class="highlight">knowledge</span> and <span class="highlight">confidence</span> I gained directly helped me during placements. I was <span class="highlight">better prepared</span>, more articulate, and more confident in interviews - and it ultimately played a key role in <span class="highlight">helping me secure my job</span>.',
                ),
                name: 'Adarsh A.',
            },
            {
                id: 'richard-h',
                quote: gettext(
                    'Personal Plan helped me take an <span class="highlight">honest look at my skills</span> and see where I had room to grow. The <span class="highlight">assessments</span> gave me a clear path forward, helping me keep pace with rapid changes in AI and <span class="highlight">stay ahead in my career</span>.',
                ),
                name: 'Richard H.',
            },
            {
                id: 'weston-d',
                quote: gettext(
                    'The <span class="highlight">value</span> goes beyond simply learning something new. It helped me <span class="highlight">understand myself better</span>, think more intentionally about my future, and <span class="highlight">build skills I can continue using</span> as my goals and circumstances change.',
                ),
                name: 'Weston D.',
            },
        ],
        [gettext],
    );

    const pagerEventTarget = useMemo(() => new EventTarget(), []);

    const [pagerState, setPagerState] = useState({
        pageCount: testimonials.length,
        activeIndex: 0,
        isLastPage: false,
    });

    const handleMutation = useCallback((event: CarouselMutationEvent) => {
        setPagerState((current) => {
            const next = {
                pageCount: event.pageCount ?? current.pageCount,
                activeIndex: event.visiblePageIndex ?? 0,
                isLastPage: event.isLastPage,
            };

            return current.pageCount === next.pageCount &&
                current.activeIndex === next.activeIndex &&
                current.isLastPage === next.isLastPage
                ? current
                : next;
        });
    }, []);

    const handlePreviousClick = useCallback(
        () => dispatchCarouselPreviousPageEvent(pagerEventTarget),
        [pagerEventTarget],
    );

    const handleNextClick = useCallback(
        () => dispatchCarouselNextPageEvent(pagerEventTarget),
        [pagerEventTarget],
    );

    const slides = testimonials.map((testimonial) => (
        <CarouselSlide
            key={testimonial.id}
            className={styles['testimonial-slide']}
            aria-label={testimonial.name}
        >
            <BlockQuoteCard
                color="gray"
                className={styles['testimonial-card']}
                attribution={testimonial.name}
            >
                <LocalizedHtml
                    html={testimonial.quote}
                    interpolate={{highlight: <span className="ud-text-bold" />}}
                />
            </BlockQuoteCard>
        </CarouselSlide>
    ));

    return (
        <div className={styles['testimonials-section']}>
            <div className={styles['testimonials-container']}>
                <h2 className={classNames('ud-heading-xxl', styles['testimonials-title'])}>
                    {gettext('What other learners are saying')}
                </h2>
                <CarouselWithFocusOrder
                    className={styles['testimonials-row']}
                    ariaLabel={gettext('Learner testimonials')}
                    slides={slides}
                    allowScroll={true}
                    showPager={false}
                    gridFullWidthItems={true}
                    pageByFullWidth={true}
                    pagerEventTarget={pagerEventTarget}
                    onMutation={handleMutation}
                />
                <div className={styles['testimonials-pager']}>
                    <IconButton
                        udStyle="white-solid"
                        size="xsmall"
                        round={true}
                        disabled={pagerState.activeIndex === 0}
                        onClick={handlePreviousClick}
                        aria-label={gettext('Previous testimonial')}
                    >
                        <PreviousIcon label={false} />
                    </IconButton>
                    <IndicatorDots
                        pageCount={pagerState.pageCount}
                        activeIndex={pagerState.activeIndex}
                    />
                    <IconButton
                        udStyle="white-solid"
                        size="xsmall"
                        round={true}
                        disabled={pagerState.isLastPage}
                        onClick={handleNextClick}
                        aria-label={gettext('Next testimonial')}
                    >
                        <NextIcon label={false} />
                    </IconButton>
                </div>
            </div>
        </div>
    );
};
